import { useEffect, useState } from 'react';
import { Box, Image, Text, useColorModeValue } from '@chakra-ui/react';
import { MdPhotoCamera } from 'react-icons/md';
import {
  CameraSnapshotSubject,
  useGetCameraSnapshotUrl,
} from '../hooks/useGetCameraSnapshotUrl';

interface Props {
  agentId: string;
  organizationId: string;
  /** The camera: its MAC when the box knows it, else the court it records. `null` = nothing to ask for. */
  subject: CameraSnapshotSubject | null;
  /** When the box last uploaded a frame. Undefined = no picture exists yet; nothing is fetched. */
  lastSnapshotAt?: string;
  /** Rendered inside the frame when there is no picture to show. */
  placeholder: string;
  /** Rendered when a URL was minted but the image failed to load (expired, store flipped). */
  failedText: string;
  alt: string;
  /** Height of the 16:9 frame; width follows the container. */
  height?: string | number;
}

/**
 * The picture next to a court. A fixed 16:9 frame so the layout does not jump
 * between "no picture", "loading" and "loaded"; the URL is a short-lived
 * signed read handed over by `useGetCameraSnapshotUrl` and rendered straight
 * into an `<img>` — never stored, never parsed (the opaque-URL contract every
 * court-recording media read follows).
 *
 * `onError` is the honest fallback for the two ways a minted URL can still
 * fail to load: the 5-minute signature ran out on a page left open, or the
 * video store was flipped between the capture and the read. Both resolve
 * themselves on the next capture, so the text says to take a new picture
 * rather than describing storage.
 */
export default function CameraSnapshot({
  agentId,
  organizationId,
  subject,
  lastSnapshotAt,
  placeholder,
  failedText,
  alt,
  height = '150px',
}: Props) {
  const frameBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const { data, isError } = useGetCameraSnapshotUrl(
    agentId,
    organizationId,
    subject,
    lastSnapshotAt,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  // A new URL (a fresh capture) gets a fresh chance to load.
  useEffect(() => setLoadFailed(false), [data?.url]);

  const showImage = Boolean(lastSnapshotAt && data?.url && !loadFailed && !isError);
  const message = lastSnapshotAt && (loadFailed || isError) ? failedText : placeholder;

  return (
    <Box
      position="relative"
      w="100%"
      h={height}
      borderRadius="md"
      overflow="hidden"
      bg={frameBg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      data-testid="camera-snapshot"
      data-state={showImage ? 'image' : 'placeholder'}
    >
      {showImage ? (
        <Image
          src={data?.url}
          alt={alt}
          w="100%"
          h="100%"
          objectFit="cover"
          onError={() => setLoadFailed(true)}
        />
      ) : (
        <Box textAlign="center" px={3} color={mutedText}>
          <MdPhotoCamera size={28} style={{ margin: '0 auto 4px' }} />
          <Text fontSize="xs">{message}</Text>
        </Box>
      )}
    </Box>
  );
}
